import { useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark-dimmed.css';

import { submitQueryFeedback } from '../api';
import type { QueryResult } from '../types';
import { formatAnswerCitations } from '../utils';

export interface QueryHistoryItem {
  question: string;
  result: QueryResult;
}

export function QueryResultCard({ item }: { item: QueryHistoryItem }) {
  const [openChunkId, setOpenChunkId] = useState<string | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(true);
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [feedback, setFeedback] = useState<1 | -1 | null>(null);

  const hasReasoning = Boolean(item.result.reasoning);

  const handleFeedback = async (value: 1 | -1) => {
    if (!item.result.queryLogId) return;
    setFeedback(value);
    try {
      await submitQueryFeedback(item.result.queryLogId, value);
    } catch {
      // Non-fatal — feedback is best-effort
    }
  };

  return (
    <div className="lc-surface overflow-hidden border border-rule">
      {/* Question */}
      <div className="flex items-start gap-3 border-b border-rule-soft px-5 py-4">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent">
          Q
        </span>
        <p className="text-sm font-medium text-link">{item.question}</p>
      </div>

      {/* Thinking (reasoning) — collapsible */}
      {hasReasoning && (
        <div className="border-b border-rule-soft">
          <button
            type="button"
            onClick={() => setThinkingOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-5 py-2.5 text-left text-xs font-medium text-accent transition-colors hover:text-accent/80"
          >
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            Thinking
            <svg
              aria-hidden="true"
              className={`ml-auto h-3 w-3 transition-transform ${thinkingOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {thinkingOpen && (
            <div className="border-t border-rule-soft bg-accent-soft/40 px-5 py-3">
              <pre className="max-h-60 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-accent-ink/90">
                {item.result.reasoning}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Answer */}
      <div className="flex items-start gap-3 px-5 py-4">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-good/15 text-xs font-bold text-good">
          A
        </span>
        <div className="lc-answer min-w-0 flex-1">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
            {formatAnswerCitations(item.result.answer)}
          </Markdown>
        </div>
      </div>

      {/* Feedback buttons */}
      {item.result.queryLogId && (
        <div className="flex items-center gap-1.5 border-t border-rule-soft px-5 py-2">
          <span className="text-3xs font-medium uppercase tracking-wider text-dim">Helpful?</span>
          <button
            type="button"
            data-testid="feedback-up"
            onClick={() => void handleFeedback(1)}
            disabled={feedback !== null}
            className={`rounded p-1 transition-colors ${
              feedback === 1 ? 'text-good' : 'text-dim hover:text-good'
            } disabled:cursor-default`}
            title="Yes, this was helpful"
            aria-label="Yes, this was helpful"
          >
            <svg
              aria-hidden="true"
              className="h-4 w-4"
              fill={feedback === 1 ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V3a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m7.723-9.132c.065.078.13.155.193.232a11.955 11.955 0 0 1 2.649 7.521c0 .2-.006.4-.018.598"
              />
            </svg>
          </button>
          <button
            type="button"
            data-testid="feedback-down"
            onClick={() => void handleFeedback(-1)}
            disabled={feedback !== null}
            className={`rounded p-1 transition-colors ${
              feedback === -1 ? 'text-del' : 'text-dim hover:text-del'
            } disabled:cursor-default`}
            title="No, this wasn't helpful"
            aria-label="No, this wasn't helpful"
          >
            <svg
              aria-hidden="true"
              className="h-4 w-4"
              fill={feedback === -1 ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715A12.137 12.137 0 0 1 2.25 12c0-2.848.992-5.464 2.649-7.521C5.287 3.997 5.886 3.75 6.504 3.75h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384"
              />
            </svg>
          </button>
          {feedback !== null && (
            <span className="ml-1 text-3xs text-dim">Thanks for the feedback!</span>
          )}
        </div>
      )}

      {/* Resolved version badge */}
      {item.result.resolvedVersion && (
        <div className="flex items-center gap-1.5 px-5 pb-3">
          <span className="text-3xs font-medium uppercase tracking-wider text-dim">Source</span>
          <span className="rounded bg-bg px-1.5 py-0.5 font-mono text-3xs text-dim">
            {item.result.resolvedPackage}@{item.result.resolvedVersion}
          </span>
        </div>
      )}

      {/* Sources */}
      {item.result.chunks.length > 0 && (
        <div className="border-t border-rule-soft">
          <button
            type="button"
            onClick={() => setSourcesOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-dim transition-colors hover:text-ink"
          >
            <svg
              aria-hidden="true"
              className={`h-3.5 w-3.5 shrink-0 transition-transform ${sourcesOpen ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            Sources
            <span className="ml-auto border border-rule-soft bg-bg px-2 py-0.5 text-3xs text-dim">
              {item.result.chunks.length}
            </span>
          </button>

          {sourcesOpen && (
            <div className="px-5 pb-4">
              <div className="flex flex-wrap gap-2">
                {item.result.chunks.map((chunk) => (
                  <button
                    key={chunk.id}
                    type="button"
                    title={chunk.title}
                    onClick={() =>
                      setOpenChunkId((current) => (current === chunk.id ? null : chunk.id))
                    }
                    className={`inline-flex items-center gap-1.5 border px-3 py-1 text-xs font-medium transition-colors ${
                      openChunkId === chunk.id
                        ? 'border-accent bg-accent-soft text-accent'
                        : 'border-rule bg-panel text-link hover:border-accent hover:text-accent'
                    }`}
                  >
                    <span className="font-mono">{chunk.id}</span>
                    {chunk.title && (
                      <span className="max-w-[120px] truncate opacity-60">{chunk.title}</span>
                    )}
                  </button>
                ))}
              </div>

              {item.result.chunks.map((chunk) =>
                openChunkId === chunk.id ? (
                  <div key={chunk.id} className="lc-code-panel mt-3 p-4 text-sm">
                    <p className="mb-2 text-xs font-semibold text-ink">{chunk.title}</p>
                    <pre className="max-h-80 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-link">
                      {chunk.chunk}
                    </pre>
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
